import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader } from 'lucide-react';
import { AppLayout } from '../../components/layout/AppLayout';
import { AssistantsSidebar, AssistantCategory } from '../../components/assistants/AssistantsSidebar';
import { CreateAssistantForm, AssistantFormData } from '../../components/assistants/CreateAssistantForm';
import { Button, BackButton } from '../../components/ui';
import { mockCategories } from '../../data/mockData';
import api from '../../services/api';
import styles from './CreateAssistantPage.module.css';

export const CreateAssistantPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Sidebar state
  const [selectedCategory, setSelectedCategory] = useState<AssistantCategory>('marketplace');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [createdByMe, setCreatedByMe] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = () => {
    navigate('/assistants');
  };

  const handleSave = async (data: AssistantFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Step 1: Create the assistant
      const assistantResponse = await api.createAssistantWithDatasources({
        name: data.name,
        description: data.description,
        systemInstructions: data.systemInstructions,
        llmModel: data.llmModel,
        temperature: data.temperature,
        categories: data.categories,
        conversationStarters: data.conversationStarters,
      });

      if (!assistantResponse.success) {
        throw new Error('Failed to create assistant');
      }

      const assistant = assistantResponse.data;

      // Step 2: Upload files as datasources (if any) - async processing
      const filesToUpload = data.uploadedFiles.filter(f => f.status === 'pending');
      const uploadPromises: Promise<void>[] = [];
      
      for (const uploadedFile of filesToUpload) {
        const uploadTask = async () => {
          try {
            // Create datasource for the file
            const dsResponse = await api.createDatasource(assistant.id, {
              name: uploadedFile.file.name,
              description: `Uploaded document: ${uploadedFile.file.name}`,
              type: 'file',
            });

            if (dsResponse.success) {
              // Upload the file to the datasource (processing happens async in background)
              await api.uploadDocument(
                assistant.id,
                dsResponse.data.id,
                uploadedFile.file,
                (progress) => {
                  console.log(`Upload progress for ${uploadedFile.file.name}: ${progress}%`);
                }
              );
              console.log(`File ${uploadedFile.file.name} uploaded - processing in background`);
            }
          } catch (uploadError) {
            console.error(`Failed to upload ${uploadedFile.file.name}:`, uploadError);
            // Continue with other files even if one fails
          }
        };
        uploadPromises.push(uploadTask());
      }

      // Wait for all uploads to be submitted (not processed - that's async)
      await Promise.all(uploadPromises);

      // Navigate to the newly created assistant
      navigate(`/assistants/${assistant.id}`);
    } catch (err) {
      console.error('Error creating assistant:', err);
      setError(err instanceof Error ? err.message : 'Failed to create assistant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/assistants');
  };

  const handleRefineWithAI = () => {
    console.log('Refine with AI');
  };

  const handleSaveClick = () => {
    const form = document.getElementById('create-assistant-form') as HTMLFormElement;
    if (form) form.requestSubmit();
  };

  const sidebar = (
    <AssistantsSidebar
      selectedCategory={selectedCategory}
      onCategoryChange={setSelectedCategory}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      selectedProject={selectedProject}
      onProjectChange={setSelectedProject}
      categories={mockCategories}
      selectedCategories={selectedCategories}
      onCategoriesChange={setSelectedCategories}
      createdByMe={createdByMe}
      onCreatedByMeChange={setCreatedByMe}
      selectedCreator={selectedCreator}
      onCreatorChange={setSelectedCreator}
    />
  );

  return (
    <AppLayout sidebar={sidebar}>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div className={styles.pageHeaderLeft}>
            <BackButton onClick={handleBack} />
            <h1 className={styles.pageTitle}>Create Assistant</h1>
          </div>
          <div className={styles.pageHeaderRight}>
            <Button
              type="button"
              variant="primary"
              leftIcon={<Sparkles size={16} />}
              onClick={handleRefineWithAI}
              className={styles.refineButton}
              disabled={isSubmitting}
            >
              Refine with AI
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="primary" 
              onClick={handleSaveClick}
              disabled={isSubmitting}
              leftIcon={isSubmitting ? <Loader size={16} className={styles.spinning} /> : undefined}
            >
              {isSubmitting ? 'Creating...' : 'Save'}
            </Button>
          </div>
        </div>
        {error && (
          <div className={styles.errorBanner}>
            {error}
          </div>
        )}
        <div className={styles.pageContent}>
          <CreateAssistantForm onCancel={handleCancel} onSave={handleSave} isSubmitting={isSubmitting} />
        </div>
      </div>
    </AppLayout>
  );
};
